/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { useLogger } from '@airasia-rn/core-logger';
import { useAnalytics } from '@airasia-phoenix/react-native/analytics/analytics';
import type { MemoizedListViewProps } from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import type { CurrencyItem } from '@airasia-rn/ui/widgets';
import {
  trackCurrencySelected,
  trackLanguageSelected,
  LOCALE_CURRENCY_EVENT,
} from '../../analytics/localeCurrency';

export interface UseLocaleCurrencyAnalyticsParams {
  selectedCurrencyId: string;
  selectedLanguageId: string;
}

const useLocaleCurrencyAnalytics = ({
  selectedCurrencyId,
  selectedLanguageId,
}: UseLocaleCurrencyAnalyticsParams) => {
  const { logEvent } = useLogger();
  const { screenName, category } = useAnalytics();

  const trackCurrencySelection = useCallback(
    (item: CurrencyItem) => {
      logEvent(
        LOCALE_CURRENCY_EVENT,
        trackCurrencySelected({
          previousValue: selectedCurrencyId,
          selectedValue: item.id,
          category,
          screen_name: screenName,
        }),
      );
    },
    [category, logEvent, screenName, selectedCurrencyId],
  );

  const trackLanguageSelection = useCallback(
    (item: MemoizedListViewProps) => {
      logEvent(
        LOCALE_CURRENCY_EVENT,
        trackLanguageSelected({
          previousValue: selectedLanguageId,
          selectedValue: item.id,
          category,
          screen_name: screenName,
        }),
      );
    },
    [category, logEvent, screenName, selectedLanguageId],
  );

  return {
    trackCurrencySelection,
    trackLanguageSelection,
  };
};

export default useLocaleCurrencyAnalytics;
